/**
 * Settings → Scheduled Reports
 *
 * Configure READ-ONLY periodic training summary emails (daily/weekly/monthly).
 * Backed by /api/v1/report-schedules. org_admin+ only. Sending is handled by
 * the hourly scheduler; "Send now" + "Preview" available per-schedule.
 */
import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';
import { SkeletonCard } from '../../components/common/SkeletonCard';
import { EmptyState } from '../../components/common/EmptyState';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Card, CardContent } from '../../components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  CalendarClock, Plus, Pencil, Trash2, Send, Eye, Mail, Clock,
} from 'lucide-react';

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function cadenceSummary(s) {
  const at = `${String(s.hour).padStart(2, '0')}:00 UTC`;
  if (s.cadence === 'daily') return `Daily at ${at}`;
  if (s.cadence === 'weekly') return `Weekly · ${WEEKDAYS[s.weekday] || 'Monday'} at ${at}`;
  return `Monthly · day ${s.day_of_month} at ${at}`;
}

const EMPTY = {
  name: '', recipients: '', cadence: 'weekly', hour: 8, weekday: 0, day_of_month: 1, is_active: true,
};

export default function ScheduledReportsTab() {
  const { get, post, put, del } = useApi();
  const [rows, setRows] = useState(null);
  const [dialog, setDialog] = useState(null);   // {mode:'create'|'edit', form}
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    get('/report-schedules').then((r) => setRows(r.schedules || [])).catch(() => setRows([]));
  }, [get]);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => setDialog({ mode: 'create', form: { ...EMPTY } });
  const openEdit = (s) => setDialog({
    mode: 'edit', id: s.schedule_id,
    form: {
      name: s.name, recipients: (s.recipients || []).join(', '),
      cadence: s.cadence, hour: s.hour, weekday: s.weekday,
      day_of_month: s.day_of_month, is_active: s.is_active,
    },
  });

  const save = async () => {
    const f = dialog.form;
    const recipients = f.recipients.split(',').map((e) => e.trim()).filter(Boolean);
    if (!f.name.trim()) return toast.error('Name is required');
    if (recipients.length === 0) return toast.error('Add at least one recipient email');
    const payload = {
      name: f.name.trim(), recipients, cadence: f.cadence,
      hour: Number(f.hour), weekday: Number(f.weekday),
      day_of_month: Number(f.day_of_month), is_active: f.is_active,
    };
    setBusy(true);
    try {
      if (dialog.mode === 'create') {
        await post('/report-schedules', payload);
        toast.success('Schedule created');
      } else {
        await put(`/report-schedules/${dialog.id}`, payload);
        toast.success('Schedule updated');
      }
      setDialog(null);
      load();
    } catch (e) {
      toast.error('Could not save schedule');
    } finally { setBusy(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.name}"?`)) return;
    try { await del(`/report-schedules/${s.schedule_id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  const runNow = async (s) => {
    setBusy(true);
    try {
      const r = await post(`/report-schedules/${s.schedule_id}/run-now`, {});
      const res = r.result || {};
      toast.success(`Sent to ${res.sent || 0} recipient(s)${res.failed ? `, ${res.failed} failed` : ''}`);
      load();
    } catch { toast.error('Send failed'); }
    finally { setBusy(false); }
  };

  const openPreview = async () => {
    try { const r = await get('/report-schedules/preview'); setPreview(r.html); }
    catch { toast.error('Preview failed'); }
  };

  const setForm = (patch) => setDialog((d) => ({ ...d, form: { ...d.form, ...patch } }));

  return (
    <div className="space-y-4" data-testid="scheduled-reports-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-lg font-semibold flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-[var(--lumyn-settings)]" />Scheduled Reports
          </h2>
          <p className="text-sm text-muted-foreground">Email a read-only training summary on a daily, weekly or monthly cadence.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openPreview} data-testid="preview-report-btn">
            <Eye className="w-4 h-4 mr-1.5" />Preview
          </Button>
          <Button onClick={openCreate} data-testid="new-schedule-btn">
            <Plus className="w-4 h-4 mr-1.5" />New schedule
          </Button>
        </div>
      </div>

      {rows == null ? <SkeletonCard /> : rows.length === 0 ? (
        <EmptyState icon={CalendarClock} title="No schedules yet"
          description="Create a schedule to start emailing periodic training summaries to your team."
          action={<Button onClick={openCreate}><Plus className="w-4 h-4 mr-1.5" />New schedule</Button>} />
      ) : (
        <div className="space-y-2">
          {rows.map((s) => (
            <Card key={s.schedule_id} data-testid={`schedule-${s.schedule_id}`}>
              <CardContent className="p-4 flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{s.name}</span>
                    <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${
                      s.is_active ? 'bg-green-500/15 text-green-700 dark:text-green-400' : 'bg-muted text-muted-foreground'
                    }`}>{s.is_active ? 'active' : 'paused'}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{cadenceSummary(s)}</span>
                    <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{(s.recipients || []).length} recipient(s)</span>
                    {s.next_run_at && <span>Next: {String(s.next_run_at).slice(0, 16).replace('T', ' ')} UTC</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <Button variant="ghost" size="sm" onClick={() => runNow(s)} disabled={busy} data-testid={`run-now-${s.schedule_id}`}>
                    <Send className="w-3.5 h-3.5 mr-1" />Send now
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => openEdit(s)} data-testid={`edit-${s.schedule_id}`}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(s)} data-testid={`delete-${s.schedule_id}`}>
                    <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create / Edit dialog */}
      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-md" data-testid="schedule-dialog">
          <DialogHeader>
            <DialogTitle>{dialog?.mode === 'create' ? 'New schedule' : 'Edit schedule'}</DialogTitle>
          </DialogHeader>
          {dialog && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input value={dialog.form.name} onChange={(e) => setForm({ name: e.target.value })}
                  placeholder="Weekly Ops Summary" data-testid="schedule-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Recipients (comma-separated emails)</Label>
                <Input value={dialog.form.recipients} onChange={(e) => setForm({ recipients: e.target.value })}
                  placeholder="lead@org.com, manager@org.com" data-testid="schedule-recipients-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Cadence</Label>
                  <Select value={dialog.form.cadence} onValueChange={(v) => setForm({ cadence: v })}>
                    <SelectTrigger data-testid="schedule-cadence-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Hour (UTC)</Label>
                  <Input type="number" min={0} max={23} value={dialog.form.hour}
                    onChange={(e) => setForm({ hour: e.target.value })} data-testid="schedule-hour-input" />
                </div>
              </div>
              {dialog.form.cadence === 'weekly' && (
                <div className="space-y-1.5">
                  <Label>Day of week</Label>
                  <Select value={String(dialog.form.weekday)} onValueChange={(v) => setForm({ weekday: v })}>
                    <SelectTrigger data-testid="schedule-weekday-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {WEEKDAYS.map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {dialog.form.cadence === 'monthly' && (
                <div className="space-y-1.5">
                  <Label>Day of month (1–28)</Label>
                  <Input type="number" min={1} max={28} value={dialog.form.day_of_month}
                    onChange={(e) => setForm({ day_of_month: e.target.value })} data-testid="schedule-dom-input" />
                </div>
              )}
              <div className="flex items-center justify-between">
                <Label>Active</Label>
                <Switch checked={dialog.form.is_active} onCheckedChange={(v) => setForm({ is_active: v })}
                  data-testid="schedule-active-switch" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button onClick={save} disabled={busy} data-testid="schedule-save-btn">
              {busy ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview dialog */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-lg" data-testid="preview-dialog">
          <DialogHeader><DialogTitle>Report preview</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-auto rounded-lg border border-border p-3 bg-white"
            dangerouslySetInnerHTML={{ __html: preview || '' }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
